import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MenuOption, ReactState } from './index'
import { EDGE_BUFFER } from '../../../../../NonReact/constants/Misc'
import Icon, { IconName } from '../../Icon'
import Loader from '../../Loader'
import MenuItemIcon from '../../MenuItemIcon/MenuItemIcon'
import StandardMenuEntry from './StandardMenuEntry'
import { OVERFLOW_VALUES, MAX_SUBMENU_SIZE } from '../Constants'
import CScrollBarWidth from '../../../../../NonReact/DOM/ScrollBarWidth'

type Props = {
    menuOption: MenuOption
}

/**
 * Sub menu entry is the entry that includes a sub menu.
 *
 * @param menuOption - Javascript object representation of the sub menu entry.
 */
const SubMenuEntry: React.FC<Props> = ({ menuOption }: Props): ReactElement => {
    const { title, tooltip, icon, submenu, onHover, isEnabled, isVisible, loading }: MenuOption = menuOption
    const [isOpen, setIsOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isStyled, setStyled]: ReactState<boolean> = useState<boolean>(false)
    const [submenuStyles, setSubmenuStyles]: ReactState<Definable<React.CSSProperties>> = useState<
        Definable<React.CSSProperties>
    >({ opacity: 0 })

    const submenuEl: React.MutableRefObject<HTMLUListElement | null> = useRef(null)

    useLayoutEffect(() => {
        if (isOpen && submenuEl.current && !isStyled && submenu && submenu.length > 0) {
            setStyled(true)

            const windowLeft: number = 0
            const windowBottom: number = window.innerHeight
            const windowRight: number = window.innerWidth

            const submenuBoundingRects: DOMRect = submenuEl.current.getBoundingClientRect()
            const submenuLeft: number = submenuBoundingRects.left
            const submenuRight: number = submenuBoundingRects.right
            const submenuBottom: number = submenuBoundingRects.bottom

            const right: Definable<string> =
                windowRight < submenuRight ? `${submenuRight - windowRight + EDGE_BUFFER}px` : undefined
            const left: Definable<string> =
                windowLeft > submenuLeft ? `${Math.abs(submenuLeft) + EDGE_BUFFER}px` : undefined
            const bottom: Definable<string> =
                windowBottom < submenuBottom ? `${-(submenuBottom - windowBottom + EDGE_BUFFER)}px` : undefined
            // Enrich previous styles instead of overriding them,
            // which could effectively remove overflow-y value set in the useEffect bellow
            setSubmenuStyles((prev: Definable<React.CSSProperties>) => ({
                ...prev,
                opacity: undefined,
                left,
                right,
                top: bottom
            }))
        }
    }, [isOpen, submenu])

    /* Whenever submenu length changes we need to compute overflowY value,
     * e.g. ACD Not Available codes submenu initially contains single [No Reason] value
     * and eventually is populated with remaining values
     */
    useEffect(() => {
        setSubmenuStyles((prev: Definable<React.CSSProperties>) => ({
            ...prev,
            overflowY:
                submenu?.length !== undefined && submenu.length > MAX_SUBMENU_SIZE
                    ? OVERFLOW_VALUES.AUTO
                    : OVERFLOW_VALUES.INITIAL
        }))
    }, [submenu?.length])

    return isVisible ? (
        <li
            className={`om-entry ome-with-submenu ${isOpen ? 'submenu-open' : ''}`}
            title={tooltip}
            onMouseOver={(): void => setIsOpen(true)}
            onMouseOut={(): void => setIsOpen(false)}
        >
            <a
                className={`ome-anchor ${isEnabled ? '' : 'disabled'}`}
                onMouseOver={onHover}
                onMouseDown={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
                    if (menuOption?.isEnabled && menuOption?.onClick) {
                        menuOption.onClick(e)
                    }
                    e.nativeEvent.stopPropagation()
                    e.nativeEvent.preventDefault()
                }}
            >
                {menuOption?.renderedIcon ? menuOption.renderedIcon : <MenuItemIcon iconName={icon} />}
                <span className='omea-title'>{title}</span>
                {submenu ? <>{loading ? <Loader size={16} /> : <Icon iconName={IconName.ARROW_RIGHT} />}</> : <></>}
            </a>
            <ul
                className='ome-submenu'
                ref={submenuEl}
                style={submenuStyles}
                onMouseDown={(e: React.MouseEvent<HTMLUListElement, MouseEvent>): void => {
                    const target: HTMLUListElement = e.target as HTMLUListElement

                    /* The code block is checking if the submenu has a vertical scrollbar and if the user
                clicked on the scrollbar area. If both conditions are true, it stops the event
                propagation, preventing any further actions from being triggered. This is done to
                ensure that clicking on the scrollbar does not close the submenu or trigger any
                unintended actions. */
                    if (
                        target.scrollHeight > target.offsetHeight &&
                        target.getBoundingClientRect().right - e.clientX < CScrollBarWidth.getScrollBarWidth()
                    ) {
                        e.stopPropagation()
                    }
                }}
            >
                {submenu?.map((option: MenuOption, index: number): ReactElement => {
                    if (!option.renderedSubMenu) {
                        return option.submenu ? (
                            <SubMenuEntry key={index} menuOption={option} />
                        ) : (
                            <StandardMenuEntry key={index} menuOption={option} />
                        )
                    }

                    return option.renderedSubMenu
                })}
            </ul>
        </li>
    ) : (
        <></>
    )
}

export default SubMenuEntry
